import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SEOHead } from "@/components/SEOHead";
import { ProductCard } from "@/components/ProductCard";
import { ArrowRight, MessageCircle } from "lucide-react";

const HERO = "https://images.weserv.nl/?url=customer-assets-4nw71qhi.emergentagent.net/job_bread-market-5/artifacts/duoofxnm_cocoamaya%20website%20banner.png&w=2000&q=88&output=webp";
const HERO_MOBILE = "https://images.weserv.nl/?url=customer-assets-4nw71qhi.emergentagent.net/job_bread-market-5/artifacts/xxum8sq3_cocoamaya%20mobile%20banner%20.png&w=900&q=88&output=webp";
const SPECIALTY_CAKE = "https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_bread-market-5/artifacts/2gzga08h_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2002_47_08%20PM.png&w=1200&h=900&fit=cover&q=82&output=webp";
const SPECIALTY_PASTRY = "https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_bread-market-5/artifacts/s5cyyaoy_savory%20and%20breads.png&w=1200&h=900&fit=cover&q=82&output=webp";
const SPECIALTY_HAMPER = "https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_bread-market-5/artifacts/i9am5wl3_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2003_24_02%20PM.png&w=1200&h=900&fit=cover&q=82&output=webp";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  // Legacy WordPress search-template URLs (e.g. /?s=cake or /?s={search_term_string})
  // still resolve to our SPA but should not be indexed as duplicate homepages.
  const hasLegacySearch =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("s");

  useEffect(() => {
    api.get("/products?featured=true").then((r) => {
      // Sort with Black Forest Croissant first (feature the newest addition),
      // then remaining featured items. Show up to 8 on the homepage grid.
      const list = r.data || [];
      list.sort((a, b) => {
        if (a.name === "Black Forest Croissant") return -1;
        if (b.name === "Black Forest Croissant") return 1;
        return 0;
      });
      setFeatured(list.slice(0, 8));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEOHead
        title="Cocoamaya Patisserie — Fresh Cakes, Pastries & Desserts in Mumbai"
        description="Order fresh cakes, French pastries, macarons and artisan desserts online. Same-day delivery across Mumbai. Est. 2017 · Andheri East."
        url="https://cocoamayapatisserie.com/"
        keywords="cake shop mumbai, patisserie mumbai, buy cake online mumbai, french pastries mumbai, macarons mumbai, artisan bakery andheri, same day cake delivery mumbai, gourmet desserts mumbai, cocoamaya patisserie"
        noindex={hasLegacySearch}
      />
      {/* Hero — single full-width banner image (text + CTA baked in) */}
      <section className="px-4 md:px-8 pt-6">
        {/* Mobile banner (portrait) */}
        <Link
          to="/shop"
          className="block md:hidden max-w-7xl mx-auto rounded-3xl overflow-hidden group"
          style={{ background: "var(--bg-card)" }}
          data-testid="hero-banner-mobile"
          aria-label="Shop Cocoamaya — Fresh Cakes & Artisan Desserts"
        >
          <img
            src={HERO_MOBILE}
            alt="Cocoamaya — Fresh Cakes & Artisan Desserts. Exquisite patisserie in the heart of Mumbai."
            loading="eager"
            fetchpriority="high"
            className="w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
          />
        </Link>
        {/* Desktop banner (landscape) */}
        <Link
          to="/shop"
          className="hidden md:block max-w-7xl mx-auto rounded-3xl overflow-hidden group"
          style={{ background: "var(--bg-card)" }}
          data-testid="hero-banner"
          aria-label="Shop Cocoamaya — Fresh Cakes & Artisan Desserts"
        >
          <img
            src={HERO}
            alt="Cocoamaya — Fresh Cakes & Artisan Desserts. Exquisite patisserie in the heart of Mumbai."
            loading="eager"
            fetchpriority="high"
            className="w-full aspect-[16/9] object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
          />
        </Link>
      </section>

      {/* Specialties */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <h2 className="font-display text-4xl md:text-5xl text-center mb-14 section-leaf italic">
          Our Specialties
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            { img: SPECIALTY_CAKE, title: "Signature Cakes", desc: "Elegant custom-crafted cakes for all occasions", to: "/shop?category=cakes", external: false, cta: "Browse All" },
            { img: SPECIALTY_PASTRY, title: "Artisan Savouries", desc: "Buttery croissants, savoury focaccias & flaky puffs — fresh from the oven daily.", to: "/shop?category=artisan-savouries", external: false, cta: "Browse All" },
            { img: SPECIALTY_HAMPER, title: "Gift Hampers", desc: "Curated festive & corporate hampers — patisserie favourites beautifully boxed.", to: "/gift-hampers", external: false, cta: "Explore Hampers" },
          ].map((s) => {
            const Wrap = ({ children }) => s.external ? (
              <a href={s.to} target="_blank" rel="noreferrer" className="btn-secondary mt-6 text-sm" style={{ padding: "0.55rem 1.4rem" }}>
                {children}
              </a>
            ) : (
              <Link to={s.to} className="btn-secondary mt-6 text-sm" style={{ padding: "0.55rem 1.4rem" }}>
                {children}
              </Link>
            );
            return (
              <div
                key={s.title}
                className="rounded-2xl overflow-hidden p-6 flex flex-col items-center text-center"
                style={{ background: "var(--bg-card)" }}
                data-testid={`specialty-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="w-full aspect-[4/3] overflow-hidden rounded-xl">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl mt-6 italic">{s.title}</h3>
                <p className="mt-3 max-w-xs" style={{ color: "var(--ink-soft)" }}>{s.desc}</p>
                <Wrap>
                  {s.cta} <ArrowRight size={14} strokeWidth={1.5} />
                </Wrap>
              </div>
            );
          })}
        </div>
      </section>

      {/* Best Sellers */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
          <h2 className="font-display text-4xl md:text-5xl text-center mb-14 section-leaf italic">
            Best Sellers
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="flex justify-center mt-12">
            <Link to="/shop" className="btn-secondary" data-testid="view-all-btn">
              View All Products
            </Link>
          </div>
        </section>
      )}

      {/* CTA strip */}
      <section style={{ background: "var(--bg-warm)" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24 text-center">
          <h2 className="font-display text-4xl md:text-5xl italic" style={{ color: "var(--ink)" }}>
            Handcrafted Goodness, Delivered to You
          </h2>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            Call or WhatsApp us for quick and easy orders.<br/>
            Same-day delivery available in your city.
          </p>
          <a
            href="https://wa.me/919819300124"
            target="_blank"
            rel="noreferrer"
            className="btn-primary mt-8 inline-flex"
            data-testid="whatsapp-cta"
          >
            <MessageCircle size={16} strokeWidth={1.5} /> Call / WhatsApp to Order
          </a>
        </div>
      </section>
    </div>
  );
}
